import { defaultTxt } from '@miq/utils';
import { ReportRecord } from '../../../../../models/report.model';
import { TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import {
  getAreaLeased,
  getAskingRent,
  getBaseRent,
  getBuiltReno,
  getClass,
  getEffectiveRent,
  getEscalations,
  getFirstElementOrGiven,
  getFormatedUnicValueInArrayAsString,
  getFormattedAddress,
  getFreeRentMonths,
  getLeaseImage,
  getLeaseTerm,
  getLocation,
  getOPEX,
  getpPropertySize,
  getPropertySubType,
  getRentSteps,
  getSuites,
  getTenantRep,
  getTenat,
  getTIA,
  isFieldVisible,
  isLessorVisible,
  LONG_LIST,
  SHORT_LIST1,
  SHORT_LIST2,
  getLessorData,
  getLessorRepData,
  getSubLessorData,
  getSubLessorRepData,
} from './us-lease-condensed-utils';

// TO DO:
// Check fields pending for API update

export const displayFnMap: TemplateFieldsDisplayFn = {
  // Property name
  556: {
    displayFn: displayFnWrapper,
  },
  // Address
  557: {
    displayFn: getFormattedAddress,
  },
  // Suite
  611: {
    displayFn: getSuites,
  },
  // Class
  564: {
    displayFn: getClass,
  },
  // Location
  558: {
    displayFn: getLocation,
  },

  // Transaction Type
  607: {
    displayFn: displayFnWrapper,
  },
  // Area leased
  567: {
    displayFn: getAreaLeased,
  },
  // Property size
  561: {
    displayFn: getpPropertySize,
  },
  // Property subtype
  562: {
    displayFn: getPropertySubType,
  },
  // Built | Reno
  566: {
    displayFn: getBuiltReno,
  },

  // Base rent
  573: {
    displayFn: getBaseRent,
  },
  // Effective rent
  600: {
    displayFn: getEffectiveRent,
  },
  // Asking rent
  601: {
    displayFn: getAskingRent,
  },
  // OPEX
  605: {
    displayFn: getOPEX,
  },
  // Escalations
  602: {
    displayFn: getEscalations,
  },

  // Sign date
  568: {
    displayFn: displayFnWrapper,
  },
  // Start date (Lease Term Start)
  570: {
    displayFn: displayFnWrapper,
  },
  // End date (Lease Term End)
  571: {
    displayFn: displayFnWrapper,
  },
  // TIA
  604: {
    displayFn: getTIA,
  },
  // Free Rent Months
  603: {
    displayFn: getFreeRentMonths,
  },

  // Lease term
  569: {
    displayFn: getLeaseTerm,
  },
  // Tenancy type
  565: {
    displayFn: displayFnWrapper,
  },
  // Parking ratio
  572: {
    displayFn: (record: ReportRecord, _, parkingRatio) => {
      const parkingRatioVal = getFirstElementOrGiven(mapAndFormatDynamicValue(record, parkingRatio));
      return parkingRatioVal != null && parkingRatioVal !== defaultTxt ? `${parkingRatioVal} / 1000` : defaultTxt;
    },
  },
  // Reserved Parking Spaces
  612: {
    displayFn: getFormatedUnicValueInArrayAsString,
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST2, LONG_LIST]),
  },
  // Seat Count
  625: {
    displayFn: getFormatedUnicValueInArrayAsString,
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST2, LONG_LIST]),
  },

  // Rent Review / Rent Steps (Escalation Details)
  606: {
    displayFn: (
      record: ReportRecord,
      options,
      escalationOrder,
      escalationValuAmount,
      escalationValuePercent,
      escalationPeriod
    ) => getRentSteps(record, options, escalationOrder, escalationValuAmount, escalationValuePercent, escalationPeriod),
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST1, LONG_LIST]),
  },
  // Tenant Industry
  624: {
    displayFn: (record: ReportRecord, _, tenantNAICSCodes) => {
      let tenantIndustryVals: string | string[] = mapAndFormatDynamicValue(record, tenantNAICSCodes);

      if (typeof tenantIndustryVals === 'string') {
        tenantIndustryVals = [tenantIndustryVals];
      }

      /**
       * From this: 111219 - Other Vegetable (except Potato) and Melon Farming
       * to get only the description: Other Vegetable (except Potato) and Melon Farming
       */
      tenantIndustryVals = tenantIndustryVals.reduce((acc: string, val: string) => {
        let valueWithoutNumber: string | string[] = val.split('- ');
        valueWithoutNumber = valueWithoutNumber[1] ? valueWithoutNumber[1] : valueWithoutNumber[0];
        return acc.length ? `${acc}, ${valueWithoutNumber}` : valueWithoutNumber;
      }, '');

      return tenantIndustryVals.length ? tenantIndustryVals : defaultTxt;
    },
    isVisible: (record: ReportRecord) => isFieldVisible(record, [SHORT_LIST1, LONG_LIST]),
  },

  // Tenant
  620: {
    displayFn: getTenat,
  },
  // Tenant Rep
  621: {
    displayFn: getTenantRep,
    isVisible: (_: ReportRecord, { reportOptions }) => {
      return !reportOptions.brokerAgency;
    },
  },
  // 622 - Lessor
  622: {
    displayFn: getLessorData,
    isVisible: isLessorVisible,
  },
  // 188 - SubLessor
  188: {
    displayFn: getSubLessorData,
    isVisible: record => !isLessorVisible(record as ReportRecord),
  },
  // 623 - Lessor Rep
  623: {
    displayFn: getLessorRepData,
    isVisible: isLessorVisible,
  },
  // 187 - SubLessor Rep
  187: {
    displayFn: getSubLessorRepData,
    isVisible: (record, settings) => {
      if (settings?.reportOptions?.brokerAgency) {
        return !settings?.reportOptions?.brokerAgency;
      }
      return !isLessorVisible(record as ReportRecord);
    },
  },
  // Notes
  608: {
    displayFn: displayFnWrapper,
  },
  /*
   * NOTE:(gaby): we were supposed to use 98 & 99 for the Lease images
   * and 528 & 527 for property images. However, due to existing images logic
   * implemented in the report-field-formatter.service, we cannot use 98 & 99 to
   * return the custom data and data structure we need for each availabilities.
   *
   * Therefore, 98 & 99 will be used for property images & 528 & 527 for the
   * availabilities images
   */
  527: {
    displayFn: getLeaseImage,
  },
  895: {
    // # - Total Land Area
    displayFn: displayFnWrapper,
  },
};
